import * as React from 'react';
import {
  AutocompleteMultiSelect,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../lib/messages';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { useSupplyAppsReferences } from './hooks';
import { AppRequestBooleanFilter, type AppRequestsListFilter } from './model';
import { toSupplyAppsOptions } from './referenceOptions';

interface AppRequestsFiltersProps {
  filter: AppRequestsListFilter;
  onPublishersChange?: ((publisherIds: string[]) => void) | undefined;
  onPlatformsChange: (platformIds: string[]) => void;
  onProcessedChange?: ((processed: AppRequestBooleanFilter) => void) | undefined;
  onBlockedChange: (blocked: AppRequestBooleanFilter) => void;
  onConnectedChange?: ((connected: AppRequestBooleanFilter) => void) | undefined;
  onBundleIdsChange: (bundleIds: string) => void;
  onAppStoreUrlsChange: (appStoreUrls: string) => void;
  publisherScoped?: boolean;
}

export function AppRequestsFilters({
  filter,
  onPublishersChange,
  onPlatformsChange,
  onProcessedChange,
  onBlockedChange,
  onConnectedChange,
  onBundleIdsChange,
  onAppStoreUrlsChange,
  publisherScoped,
}: AppRequestsFiltersProps) {
  const references = useSupplyAppsReferences({ publishersEnabled: !publisherScoped });
  const bundleId = useDebouncedValue(filter.bundleIds, onBundleIdsChange, 650);
  const appStoreUrl = useDebouncedValue(filter.appStoreUrls, onAppStoreUrlsChange, 650);
  const bundleIdInputId = React.useId();
  const appStoreUrlInputId = React.useId();

  return (
    <section
      aria-label={dashboardT('supplyApps.filters')}
      className="border-border bg-card grid min-w-0 gap-3 rounded-md border p-3 md:grid-cols-2 xl:grid-cols-4"
    >
      {onPublishersChange ? (
        <AutocompleteMultiSelect
          label={dashboardT('supplyApps.publishersFilter')}
          options={toSupplyAppsOptions(references.publishers.data ?? [])}
          value={filter.publisherIds}
          onValueChange={onPublishersChange}
          loading={references.publishers.isFetching}
          placeholder={dashboardT('supplyApps.publishersFilter')}
          emptyMessage={dashboardT('supplyApps.lookupEmpty')}
          controlClassName="bg-card"
        />
      ) : null}
      <AutocompleteMultiSelect
        label={dashboardT('supplyApps.platformsFilter')}
        options={toSupplyAppsOptions(references.platforms.data ?? [])}
        value={filter.platformIds}
        onValueChange={onPlatformsChange}
        loading={references.platforms.isFetching}
        placeholder={dashboardT('supplyApps.platformsFilter')}
        emptyMessage={dashboardT('supplyApps.lookupEmpty')}
        controlClassName="bg-card"
      />
      {onProcessedChange ? (
        <BooleanFilterSelect
          label={dashboardT('supplyApps.request.processedFilter')}
          allLabel={dashboardT('supplyApps.request.allProcessed')}
          value={filter.processed}
          yesLabel={dashboardT('supplyApps.request.processedOption')}
          noLabel={dashboardT('supplyApps.request.unprocessedOption')}
          onChange={onProcessedChange}
        />
      ) : null}
      <BooleanFilterSelect
        label={dashboardT('supplyApps.request.blockedFilter')}
        allLabel={dashboardT('supplyApps.request.allBlocked')}
        value={filter.blocked}
        yesLabel={dashboardT('supplyApps.request.blockedOption')}
        noLabel={dashboardT('supplyApps.request.notBlockedOption')}
        onChange={onBlockedChange}
      />
      {onConnectedChange ? (
        <BooleanFilterSelect
          label={dashboardT('supplyApps.request.connectedFilter')}
          allLabel={dashboardT('supplyApps.request.allConnected')}
          value={filter.connectedWithApp}
          yesLabel={dashboardT('supplyApps.request.connectedOption')}
          noLabel={dashboardT('supplyApps.request.notConnectedOption')}
          onChange={onConnectedChange}
        />
      ) : null}
      <div className="min-w-0">
        <label htmlFor={bundleIdInputId} className="sr-only">
          {dashboardT('supplyApps.request.bundleIdsFilter')}
        </label>
        <Input
          id={bundleIdInputId}
          className="bg-card"
          placeholder={dashboardT('supplyApps.request.bundleIdsFilter')}
          value={bundleId.draft}
          onChange={(event) => {
            bundleId.setDraft(event.target.value);
          }}
        />
      </div>
      <div className="min-w-0">
        <label htmlFor={appStoreUrlInputId} className="sr-only">
          {dashboardT('supplyApps.request.appStoreUrlsFilter')}
        </label>
        <Input
          id={appStoreUrlInputId}
          className="bg-card"
          placeholder={dashboardT('supplyApps.request.appStoreUrlsFilter')}
          value={appStoreUrl.draft}
          onChange={(event) => {
            appStoreUrl.setDraft(event.target.value);
          }}
        />
      </div>
      {references.publishers.isError || references.platforms.isError ? (
        <p role="alert" className="text-destructive text-xs md:col-span-2 xl:col-span-4">
          {dashboardT('supplyApps.lookupError')}
        </p>
      ) : null}
    </section>
  );
}

function BooleanFilterSelect({
  label,
  allLabel,
  value,
  yesLabel,
  noLabel,
  onChange,
}: {
  label: string;
  allLabel: string;
  value: AppRequestBooleanFilter;
  yesLabel: string;
  noLabel: string;
  onChange: (value: AppRequestBooleanFilter) => void;
}) {
  return (
    <Select
      value={value}
      onValueChange={(next) => {
        onChange(next as AppRequestBooleanFilter);
      }}
    >
      <SelectTrigger className="bg-card w-full" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={AppRequestBooleanFilter.All}>{allLabel}</SelectItem>
        <SelectItem value={AppRequestBooleanFilter.Yes}>{yesLabel}</SelectItem>
        <SelectItem value={AppRequestBooleanFilter.No}>{noLabel}</SelectItem>
      </SelectContent>
    </Select>
  );
}
